import React, { useEffect } from "react";
import "./Service.css";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

// Import service images
import sourcingImage from "../../assets/images/sourcing.png";
import qualityImage from "../../assets/images/quality-assurance.png";
import logisticsImage from "../../assets/images/logistics.png";
import sustainabilityImage from "../../assets/images/sustainability.png";
import supportImage from "../../assets/images/customer-support.png";
import { Helmet } from "react-helmet-async";

const Services = () => {
  useScrollAnimation();

  useEffect(() => {
    // Add animation classes to elements on load
    const animateElements = document.querySelectorAll(".animate-on-load");
    animateElements.forEach((element, index) => {
      element.style.animationDelay = `${0.2 + index * 0.1}s`;
    });
  }, []);

  const servicesData = [
    {
      id: 1,
      title: "Premium Sourcing",
      description:
        "We work closely with local farmers and cooperatives to source premium-grade crops, ensuring that only the best harvests are selected for our international clients.",
      image: sourcingImage,
      icon: "🌱",
      features: [
        "Direct partnerships with Kenyan farmers",
        "Rigorous selection criteria",
        "Seasonal and sustainable harvesting",
        "Fair trade practices",
      ],
    },
    {
      id: 2,
      title: "Quality Assurance",
      description:
        "Our comprehensive quality assurance process includes careful sorting, grading, and packaging to meet the strict requirements of international markets.",
      image: qualityImage,
      icon: "✓",
      features: [
        "International quality standards compliance",
        "Laboratory testing and certification",
        "Customized packaging solutions",
        "Quality control at every stage",
      ],
    },
    {
      id: 3,
      title: "Global Logistics",
      description:
        "With a reliable logistics and shipping network, we guarantee timely delivery of products to destinations across the globe.",
      image: logisticsImage,
      icon: "🚢",
      features: [
        "Global shipping network",
        "Temperature-controlled transport",
        "Real-time tracking",
        "Customs clearance assistance",
      ],
    },
    {
      id: 4,
      title: "Sustainable Practices",
      description:
        "We prioritize sustainability, adopting eco-friendly packaging and fair trade practices that support farmers and protect the environment.",
      image: sustainabilityImage,
      icon: "🌍",
      features: [
        "Eco-friendly packaging materials",
        "Carbon footprint reduction",
        "Community development programs",
        "Environmental conservation initiatives",
      ],
    },
    {
      id: 5,
      title: "Customer Support",
      description:
        "We provide personalized customer support, maintaining clear communication from order placement to final delivery.",
      image: supportImage,
      icon: "🤝",
      features: [
        "Dedicated account management",
        "24/7 order tracking",
        "Multilingual support team",
        "After-sales service",
      ],
    },
  ];

  const processSteps = [
    {
      step: 1,
      title: "Consultation & Planning",
      description:
        "We understand your requirements and develop a customized export plan",
    },
    {
      step: 2,
      title: "Sourcing & Selection",
      description:
        "Our experts source and select the finest products from trusted farmers",
    },
    {
      step: 3,
      title: "Quality Assurance",
      description: "Rigorous quality checks and certification processes",
    },
    {
      step: 4,
      title: "Packaging & Logistics",
      description: "Secure packaging and efficient logistics arrangement",
    },
    {
      step: 5,
      title: "Delivery & Support",
      description: "Timely delivery and ongoing customer support",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Our Services | Avvoka - Exporting Fresh Produce, Tea & Coffee
        </title>
        <meta
          name="description"
          content="Discover Avvoka's export services, including sourcing, quality assurance, packaging, and global logistics for fresh produce, tea, and coffee."
        />
        <meta
          name="keywords"
          content="Avvoka services, export services Kenya, fresh produce export, tea export, coffee export, global logistics, quality sourcing"
        />
        <meta
          property="og:title"
          content="Avvoka Services | Reliable Export Solutions"
        />
        <meta
          property="og:description"
          content="Avvoka provides trusted export services with a focus on quality, sustainability, and reliable delivery of Kenyan fresh produce, tea, and coffee."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://avvoka.co.ke/services" />
        <meta
          property="og:image"
          content="https://avvoka.co.ke/assets/images/services-og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@AvvokaExports" />
        <meta
          name="theme-color"
          content="#4CAF50"
          media="(prefers-color-scheme: light)"
        />
        <meta
          name="theme-color"
          content="#2E7D32"
          media="(prefers-color-scheme: dark)"
        />
      </Helmet>

      <Header />
      <div className="services-page">
        {/* Hero Section */}
        <section className="services-hero">
          <div className="hero-background">
            <div className="parallax-img services-img-1"></div>
            <div className="overlay"></div>
          </div>
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title animate-on-load">
                Our <span className="text-accent">Services</span>
              </h1>
              <p className="hero-subtitle animate-on-load">
                Comprehensive export solutions for premium Kenyan agricultural
                products
              </p>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="services-intro">
          <div className="container">
            <div className="intro-content">
              <div className="intro-text animate-on-scroll">
                <h2>Beyond Supply: Complete Export Excellence</h2>
                <p>
                  At Avvoka, we go beyond supplying fresh produce, tea, and
                  coffee — we provide a comprehensive export service that
                  ensures our clients receive products of the highest quality,
                  delivered with efficiency and care.
                </p>
                <p>
                  Our commitment to excellence extends from the farms of Kenya
                  to your doorstep, ensuring every product meets international
                  standards while maintaining the authentic Kenyan quality that
                  sets us apart.
                </p>
              </div>
              <div className="intro-stats animate-on-scroll delay-1">
                <div className="stat-item">
                  <span className="stat-number">1000+</span>
                  <span className="stat-label">Happy Clients</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Countries Served</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">99.8%</span>
                  <span className="stat-label">Delivery Success Rate</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="services-grid-section">
          <div className="container">
            <div className="section-header animate-on-scroll">
              <h2>Our Comprehensive Services</h2>
              <p>End-to-end solutions for your agricultural export needs</p>
            </div>

            <div className="services-grid">
              {servicesData.map((service, index) => (
                <div
                  key={service.id}
                  className={`service-card animate-on-scroll delay-${
                    index % 3
                  }`}
                >
                  <div className="service-image">
                    <img src={service.image} alt={service.title} />
                    <div className="service-icon">
                      <span>{service.icon}</span>
                    </div>
                  </div>
                  <div className="service-content">
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    <ul className="service-features">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="process-section">
          <div className="container">
            <div className="section-header animate-on-scroll">
              <h2>Our Export Process</h2>
              <p>Seamless journey from farm to destination</p>
            </div>

            <div className="process-timeline">
              {processSteps.map((step, index) => (
                <div
                  key={step.step}
                  className={`process-step animate-on-scroll delay-${index}`}
                >
                  <div className="step-number">
                    <span>{step.step}</span>
                  </div>
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="why-choose-us">
          <div className="container">
            <div className="section-header animate-on-scroll">
              <h2>Why Choose Avvoka?</h2>
              <p>Excellence in every export</p>
            </div>

            <div className="advantages-grid">
              <div className="advantage-card animate-on-scroll">
                <div className="advantage-icon">⭐</div>
                <h3>Premium Quality</h3>
                <p>
                  Only the finest products selected through rigorous quality
                  checks
                </p>
              </div>
              <div className="advantage-card animate-on-scroll delay-1">
                <div className="advantage-icon">⚡</div>
                <h3>Fast Delivery</h3>
                <p>Efficient logistics ensuring timely delivery worldwide</p>
              </div>
              <div className="advantage-card animate-on-scroll delay-2">
                <div className="advantage-icon">🌿</div>
                <h3>Sustainable</h3>
                <p>
                  Environmentally conscious practices throughout our operations
                </p>
              </div>
              <div className="advantage-card animate-on-scroll delay-3">
                <div className="advantage-icon">🤝</div>
                <h3>Direct Partnerships</h3>
                <p>Direct relationships with farmers ensuring fair trade</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="services-cta">
          <div className="container">
            <div className="cta-content">
              <h2 className="animate-right">
                Ready to Experience Premium Export Services?
              </h2>
              <p className="animate-left">
                Partner with Avvoka for seamless agricultural exports from Kenya
                to your destination
              </p>
              <div className="cta-buttons animate-on-scroll delay-1">
                <button className="btn btn-primary">
                  Request a Consultation
                </button>
                <button className="btn btn-secondary">View Our Products</button>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Services;
